import { resolveApiUrl } from "../../utils/url/urlResolver";
import { getCsrfValue } from "~/api/ApiHelper";
import { type OriginCheckResult, ScanResultType } from "~/ScanDMC/models/ScanDMC";

enum FlagCodes {
  GREEN = "GREEN", // Everything okay
  RED = "RED", // Error from Origin Check, check flagType for more info
  YELLOW = "YELLOW",
  BLUE = "BLUE",
}

const COUNTERFEIT_FLAGS = ["F", "T", "A", "A2", "I"];
const UNKNOWN_FLAGS = ["U"];

type CounterfeitFlags = "F" | "T" | "A" | "A2" | "I";
type UnknownFlags = "U";

type FlagTypes = CounterfeitFlags | UnknownFlags | "C" | "X";

type ApiResult = {
  wasScannedBefore?: boolean;
  codeFlag: FlagCodes;
  flagType: FlagTypes;
  isValidCode: boolean;
  // Unused - FE manually checks if the code is from outer packaging
  isOverPackaging?: boolean;
  // Unused
  logoUrl?: string;
  productCode?: string;
};

export async function fetchScanDMCOriginCheck(dmcCode: string, force?: boolean): Promise<OriginCheckResult> {
  const apiUrl = resolveApiUrl(`/scandmcapi?forceApiCall=${!!force}`);

  const response = await fetch(apiUrl, {
    body: JSON.stringify(dmcCode),
    method: "POST",
    cache: "no-cache",
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    headers: {
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
  });
  if (!response.ok) {
    throw new Error("Error fetching data");
  }
  const data: ApiResult = await response.json();

  let resultType: ScanResultType;
  // isValidCode means only that the format is valid, not that the code is valid
  if (data.isValidCode && data.codeFlag !== FlagCodes.RED) {
    if (data.codeFlag === FlagCodes.YELLOW) {
      resultType = ScanResultType.Suspicious;
    } else if (data.wasScannedBefore) {
      resultType = ScanResultType.AlreadyScanned;
    } else {
      resultType = ScanResultType.Success;
    }
    return {
      type: resultType,
      productCode: data.productCode,
      dmcCode,
    };
  } else {
    if (COUNTERFEIT_FLAGS.includes(data.flagType)) {
      resultType = ScanResultType.Counterfeit;
    } else if (UNKNOWN_FLAGS.includes(data.flagType) || !data.isValidCode) {
      resultType = ScanResultType.UnknownCode;
    } else {
      resultType = ScanResultType.UnknownError;
    }
    return {
      type: resultType,
      dmcCode,
    };
  }
}
